// Identity epoch (issue #301). A counter that advances every time the
// signed-in identity or tenant changes (login, select/switch tenant, logout),
// plus an AbortController whose signal every apiClient request is combined
// with. A request issued under epoch N that settles after the epoch moved on
// is DROPPED (StaleIdentityError) instead of being returned to a caller that
// now belongs to a different tenant/user, and its 401 must never be read as
// "the new session expired".
//
// Deliberately NOT bumped by a same-identity token refresh (refreshSession):
// that would abort healthy in-flight requests every ~10 minutes.

export interface StaleIdentityError {
  name: 'StaleIdentityError'
  status: 0
  code: 'stale_identity'
  message: string
}

export function createStaleIdentityError(message = 'Request dropped: the signed-in identity changed'): StaleIdentityError {
  return { name: 'StaleIdentityError', status: 0, code: 'stale_identity', message }
}

export function isStaleIdentityError(err: unknown): err is StaleIdentityError {
  return !!err && typeof err === 'object' && (err as { name?: unknown }).name === 'StaleIdentityError'
}

let generation = 0
let controller = new AbortController()
// Set by the cross-tab guard when ANOTHER tab changed the identity this tab
// still shows. Writes are blocked until the user reloads (see apiClient).
let stale = false

export function getIdentityGeneration(): number {
  return generation
}

export function identitySignal(): AbortSignal {
  return controller.signal
}

/** Advance the epoch and abort everything issued under the previous one. */
export function bumpIdentityGeneration(): void {
  generation += 1
  const previous = controller
  controller = new AbortController()
  previous.abort()
}

export function isIdentityStale(): boolean {
  return stale
}

export function setIdentityStale(value: boolean): void {
  stale = value
}
